import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { CerbosPermissionStatus, PermissionStore } from '../../../stores/permission-store';
import { FeatureFlagsManager } from '../../../utils/core/feature-flags-manager';
import { publishNotification } from '../../../utils/notifications/publish-notification';
import { ProjectContext } from '../../state/project-state/project-state';
import { useOptionalUserState } from '../../state/user-state/user-state';
import { Button } from '../button/button';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { Select } from '../select/select';
import { Stack } from '../stack/stack';
import { useFetchAllCompaniesQuery } from './use-fetch-all-companies-query';

type CompanyOption = {
  id: string;
  name: string;
};

const ProjectTransferRequest = () => {
  const { t } = useTranslation();
  const { project } = React.useContext(ProjectContext);
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;
  const cerbosAccessCheck = PermissionStore.instance.checkAccessToView(
    'view:system_project_transfer',
  );

  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string>('');

  const { companies, buildErrorList, isLoading: isLoadingCompanies } = useFetchAllCompaniesQuery();

  const companiesOptions: CompanyOption[] = React.useMemo(() => {
    if (!companies) {
      return [];
    }
    return companies.map((company) => ({
      id: company.id,
      name: company.name ?? '',
    }));
  }, [companies]);

  const mutation = useMutation({
    mutationFn: async (companyId: string) => {
      if (!project?.id) {
        throw new Error('Project ID is required');
      }
      const service = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      return service.sendProjectTransferRequest({
        body: {
          projectId: project.id,
          companyId,
        },
      });
    },
    onSuccess: () => {
      publishNotification('succ0312');
      setSelectedCompanyId('');
    },
    retry: false,
    networkMode: 'always',
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCompanyId || mutation.isPending) {
      return;
    }
    mutation.mutate(selectedCompanyId);
  };

  // Check if user is organization admin
  if (!isOrganizationAdmin) {
    return <Navigate replace={true} to="/projects" />;
  }

  // Check Cerbos view permission if feature flag is enabled
  if (
    FeatureFlagsManager.instance.isEnabled('SK-4477-access-management') &&
    cerbosAccessCheck !== CerbosPermissionStatus.NOT_DEFINED &&
    cerbosAccessCheck !== CerbosPermissionStatus.ACCEPT
  ) {
    return <Navigate replace={true} to="/projects" />;
  }

  if (!project) {
    return null;
  }

  const errorList = buildErrorList();

  return (
    <OverlayLoader visible={isLoadingCompanies}>
      <Stack spacing={2}>
        <h1>{t('menu.transfer.title', { ns: 'navigation' })}</h1>

        <InfoBox color="yellow" topMargin={false}>
          <Trans
            components={{
              bold: <strong />,
            }}
            i18nKey="projectTransferRequest.info"
            ns="components"
            values={{
              projectName: project.name,
            }}
          />
        </InfoBox>

        {errorList}

        {isLoadingCompanies ? (
          <InfoBox color="blue" topMargin={true}>
            {t('projectTransferRequest.loading', { ns: 'components' })}
          </InfoBox>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <LabelledContainer text="">
                <Select
                  disabled={mutation.isPending}
                  id="company-select"
                  options={companiesOptions.map((company) => ({
                    id: company.id,
                    name: company.name,
                  }))}
                  placeholderText={t('projectTransferRequest.placeholder', { ns: 'components' })}
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                />
              </LabelledContainer>

              <Button
                color="primary"
                disabled={!selectedCompanyId || mutation.isPending}
                loading={mutation.isPending}
                type="submit"
                variant="contained"
                width="100%"
              >
                {t('projectTransferRequest.submitButton', { ns: 'components' })}
              </Button>
            </Stack>
          </form>
        )}
      </Stack>
    </OverlayLoader>
  );
};

export { ProjectTransferRequest };
